/**
 * Judge.me preview-badge decorator (shared singleton).
 *
 * 用 snippet `jdgm-stars-decorate.liquid` 输出 marker，再由本脚本统一处理。
 * 一个页面无论引用多少次，本脚本只会执行一次（singleton），所有 marker
 * 共享同一份轮询 + `jdgm.starsRendered` 监听，避免移动端多脚本互相唤醒。
 *
 * 单个 marker 可读取属性：
 *   data-jdgm-scope         作用域选择器（如 "#MainProduct-xxx"）。默认 document。
 *   data-jdgm-sep           评论数前要插入的文本（如 "stars |"）。空 = 不插入。
 *   data-jdgm-suffix        评论数后要插入的文本（如 "| 10M+ members"）。空 = 不插入。
 *   data-jdgm-with-link     "1" 表示当 badge 内没有 .jdgm-review-count-link 时
 *                           从 data-number-of-reviews 自动创建（product 页用）。
 *   data-jdgm-scroll-target 点击评论数滚动目标的 CSS 选择器（逗号分隔多个候选）。
 */
(function () {
  'use strict';

  if (window.__jdgmStarsDecorateInit) return;
  window.__jdgmStarsDecorateInit = true;

  var MARKER_SELECTOR = '[data-jdgm-decorate]';
  var BADGE_SELECTOR = '.jdgm-prev-badge';
  var DECORATED_FLAG = 'jdgmDecorated';

  // 与 daad/landing-content 浮动条克隆逻辑保持兼容（它们用此标记判定可克隆）
  var LEGACY_DECORATED_FLAG = 'daadReviewDecorated';

  var POLL_MS = 250;
  var HARD_TIMEOUT_MS = 15000;
  var STABLE_AFTER_DONE_MS = 800;

  var startTime = 0;
  var pollTimer = 0;
  var stableTimer = 0;
  var hardTimer = 0;
  var disposed = false;

  function getMarkers() {
    return document.querySelectorAll(MARKER_SELECTOR);
  }

  function resolveRoot(marker) {
    var scope = marker.getAttribute('data-jdgm-scope');
    if (!scope) return document;
    var root = document.querySelector(scope);
    return root || document;
  }

  function readConfig(marker) {
    return {
      root: resolveRoot(marker),
      sep: marker.getAttribute('data-jdgm-sep') || '',
      suffix: marker.getAttribute('data-jdgm-suffix') || '',
      withLink: marker.getAttribute('data-jdgm-with-link') === '1',
      scrollTarget:
        marker.getAttribute('data-jdgm-scroll-target') ||
        '.jdgm-rev-widg,#judgeme_product_reviews',
    };
  }

  /**
   * 优先返回 badge 内已存在的 .jdgm-review-count-link（Judge.me 原生渲染）；
   * 若不存在且开启 withLink，则按 data-number-of-reviews 创建一个。
   * @returns {HTMLAnchorElement|null}
   */
  function ensureReviewLink(badge, withLink) {
    var existing = badge.querySelector('.jdgm-review-count-link');
    if (existing) return existing;
    if (!withLink) return null;

    var raw = badge.getAttribute('data-number-of-reviews');
    var count = parseInt(raw, 10);
    if (!raw || isNaN(count) || count <= 0) return null;

    var link = document.createElement('a');
    link.className = 'jdgm-review-count-link';
    link.href = '#judgeme_product_reviews';
    link.textContent = count + ' Reviews';
    badge.appendChild(link);
    return link;
  }

  /**
   * 估算页面顶部 sticky header 高度。
   * Dawn 主题 sticky 时会给 .shopify-section-header-sticky 加 .scrolled-past-header；
   * 但首次点击徽章时通常还没 scroll，因此优先按 .shopify-section-header 本身的高度算，
   * 兜底再尝试 .header-wrapper。读不到时返回 0。
   */
  function getStickyHeaderOffset() {
    var sticky =
      document.querySelector('.shopify-section-header-sticky') ||
      document.querySelector('.shopify-section-header') ||
      document.querySelector('.header-wrapper');
    if (!sticky) return 0;
    var h = sticky.getBoundingClientRect().height || 0;
    /* sticky 高度 0 时退化为 0，避免负偏移 */
    return Math.max(0, Math.round(h));
  }

  /**
   * 提前把当前视口到目标元素之间所有 `loading="lazy"` 的图片改成 eager 加载，
   * 解决「smooth 滚动过程中懒加载图陆续撑高布局，导致最终落点偏上」的问题。
   * 返回所有相关图片 decode 完成（或失败）的 Promise，便于先稳定布局再滚。
   */
  function preloadLazyMediaAboveTarget(target) {
    if (!target) return Promise.resolve();
    var targetTop = target.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop);
    var currentTop = (window.pageYOffset || document.documentElement.scrollTop);
    /* 只处理「在当前视口下方 && 在目标之上」的资源，避免无谓的整页 eager 化 */
    var lowerBound = Math.min(currentTop, targetTop);
    var upperBound = Math.max(currentTop + window.innerHeight, targetTop);

    var lazies = document.querySelectorAll('img[loading="lazy"], iframe[loading="lazy"]');
    var promises = [];
    for (var i = 0; i < lazies.length; i++) {
      var el = lazies[i];
      var rect = el.getBoundingClientRect();
      var elTop = rect.top + currentTop;
      if (elTop < lowerBound || elTop > upperBound) continue;

      try { el.loading = 'eager'; } catch (e) {}

      if (el.tagName === 'IMG' && !el.complete) {
        promises.push(new Promise(function (resolve) {
          var done = false;
          var finish = function () { if (done) return; done = true; resolve(); };
          el.addEventListener('load', finish, { once: true });
          el.addEventListener('error', finish, { once: true });
          /* 单图最长等 600ms，避免极端慢图片把整体卡死 */
          setTimeout(finish, 600);
        }));
      }
    }
    /* 全部资源最长一起等 900ms，超时也要继续滚动，保证响应性 */
    var all = Promise.all(promises);
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () { if (done) return; done = true; resolve(); };
      all.then(finish);
      setTimeout(finish, 900);
    });
  }

  function scrollToWithHeaderOffset(target, behavior) {
    if (!target) return;
    var headerOffset = getStickyHeaderOffset();
    /* 8px 透气间距，让目标标题完整露出 sticky 下沿 */
    var top =
      target.getBoundingClientRect().top +
      (window.pageYOffset || document.documentElement.scrollTop) -
      headerOffset - 8;
    if (top < 0) top = 0;
    try {
      window.scrollTo({ top: top, behavior: behavior });
    } catch (e) {
      /* 不支持平滑滚动选项的旧浏览器降级 */
      window.scrollTo(0, top);
    }
  }

  /**
   * smooth 滚动结束后再校验目标实际位置；若偏差>30px 说明动画期间布局被
   * 继续撑大了（典型场景：Judge.me 评论 widget 自身二次注入评论列表撑高），
   * 再用 instant 微调一次，最多 3 次，防抖动同时收敛误差。
   */
  function verifyAndCorrect(target) {
    var tries = 0;
    var MAX_TRIES = 3;
    var TOLERANCE = 30;
    function check() {
      if (!target.isConnected) return;
      var headerOffset = getStickyHeaderOffset();
      var rect = target.getBoundingClientRect();
      var deviation = Math.abs(rect.top - (headerOffset + 8));
      tries++;
      if (deviation > TOLERANCE && tries <= MAX_TRIES) {
        scrollToWithHeaderOffset(target, 'auto');
        setTimeout(check, 250);
      }
    }
    /* 600ms 大致等 smooth 动画收尾后再校验 */
    setTimeout(check, 600);
  }

  function bindScroll(link, scrollTarget) {
    if (link.dataset.jdgmScrollBound === '1') return;
    link.dataset.jdgmScrollBound = '1';
    link.addEventListener('click', function (e) {
      var target = null;
      var selectors = scrollTarget.split(',');
      for (var i = 0; i < selectors.length; i++) {
        target = document.querySelector(selectors[i].trim());
        if (target) break;
      }
      if (!target) return;
      e.preventDefault();

      /* 第一阶段：先把路径上的懒加载图全部转 eager 并等其布局稳定，再启动平滑滚动，
         避免「滚动目的地在点击瞬间被算定 → 滚动过程中上方图片加载撑高 → 最终落点偏上」 */
      preloadLazyMediaAboveTarget(target).then(function () {
        scrollToWithHeaderOffset(target, 'smooth');
        /* 第二阶段：动画结束后再校验目标实际位置，必要时做兜底微调 */
        verifyAndCorrect(target);
      });
    });
  }

  /**
   * 校验 badge 是否进入「数据就绪」状态：
   *   - 必须有星星节点（Judge.me 已绘制星星）
   *   - data-average-rating 必须为有效数值
   *   - .jdgm-prev-badge__text 必须为评分数字（如 "4.86"）；
   *     若 Judge.me 还没改写 __text（例如 "384 reviews" 过渡态），
   *     在 rating 有效的前提下主动改写为评分数字，避免与 sep 文本冲突。
   */
  function isBadgeReady(badge, needsText) {
    if (!badge.querySelector('.jdgm-prev-badge__stars')) return false;
    if (!needsText) return true;

    var rating = parseFloat(badge.getAttribute('data-average-rating'));
    if (isNaN(rating) || rating <= 0) return false;

    var textEl = badge.querySelector('.jdgm-prev-badge__text');
    if (!textEl) return false;

    var current = (textEl.textContent || '').trim();
    if (/^\d+(\.\d+)?$/.test(current)) return true;

    /* Judge.me 还没把 __text 改写成评分数字，由我们改写一次 */
    textEl.textContent = rating.toFixed(2);
    return true;
  }

  function decorateBadge(badge, cfg) {
    if (badge.dataset[DECORATED_FLAG] === '1') return true;

    /* 只要场景里要展示评分文本（sep/suffix 或单独 with_link 注入链接）
       就需要 __text = 评分数字，避免 Judge.me 默认 "635 reviews" 与
       我们注入的 "635 Reviews" 链接重复显示。 */
    var needsText = !!(cfg.sep || cfg.suffix || cfg.withLink);
    if (!isBadgeReady(badge, needsText)) return false;

    var link = ensureReviewLink(badge, cfg.withLink);
    /* 没有 sep/suffix 时 link 缺失也算完成（仅注入链接的场景） */
    if ((cfg.sep || cfg.suffix) && !link) return false;

    if (link) bindScroll(link, cfg.scrollTarget);

    if (cfg.sep && link) {
      var parent = link.parentNode;
      if (!parent.querySelector('.jdgm-prev-badge__sep')) {
        var sep = document.createElement('span');
        sep.className = 'jdgm-prev-badge__sep jdgm-decorate-sep';
        sep.textContent = cfg.sep;
        parent.insertBefore(sep, link);
      }
    }

    if (cfg.suffix && link) {
      var p2 = link.parentNode;
      if (!p2.querySelector('.jdgm-prev-badge__suffix')) {
        var suffix = document.createElement('span');
        suffix.className = 'jdgm-prev-badge__suffix jdgm-decorate-suffix';
        suffix.textContent = cfg.suffix;
        p2.insertBefore(suffix, link.nextSibling);
      }
    }

    badge.dataset[DECORATED_FLAG] = '1';
    badge.dataset[LEGACY_DECORATED_FLAG] = '1';
    return true;
  }

  function processOnce() {
    var markers = getMarkers();
    if (!markers.length) return true;

    var allDone = true;
    for (var i = 0; i < markers.length; i++) {
      var cfg = readConfig(markers[i]);
      var badges = cfg.root.querySelectorAll(BADGE_SELECTOR);
      if (!badges.length) {
        allDone = false;
        continue;
      }
      for (var j = 0; j < badges.length; j++) {
        if (!decorateBadge(badges[j], cfg)) allDone = false;
      }
    }
    return allDone;
  }

  function tick() {
    pollTimer = 0;
    if (disposed) return;

    if (processOnce()) {
      if (!stableTimer) stableTimer = setTimeout(dispose, STABLE_AFTER_DONE_MS);
      return;
    }

    if (Date.now() - startTime < HARD_TIMEOUT_MS) {
      pollTimer = setTimeout(tick, POLL_MS);
    } else {
      dispose();
    }
  }

  function onStarsRendered() {
    if (disposed || pollTimer) return;
    pollTimer = setTimeout(tick, 0);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    if (pollTimer) { clearTimeout(pollTimer); pollTimer = 0; }
    if (stableTimer) { clearTimeout(stableTimer); stableTimer = 0; }
    if (hardTimer) { clearTimeout(hardTimer); hardTimer = 0; }
    document.removeEventListener('jdgm.starsRendered', onStarsRendered);
    window.removeEventListener('pagehide', dispose);
  }

  function start() {
    startTime = Date.now();
    document.addEventListener('jdgm.starsRendered', onStarsRendered);
    window.addEventListener('pagehide', dispose, { once: true });
    hardTimer = setTimeout(dispose, HARD_TIMEOUT_MS);
    tick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
